import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import mollieService from "../services/mollie";
import { useCart } from "../contexts/CartContext";

const styles = {
  container: {
    maxWidth: "800px",
    margin: "0 auto",
    padding: "2rem 1rem",
  },
  card: {
    backgroundColor: "white",
    borderRadius: "0.5rem",
    boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)",
    padding: "2rem",
    marginBottom: "2rem",
  },
  icon: {
    fontSize: "3rem",
    marginBottom: "1rem",
  },
  buttonPrimary: {
    backgroundColor: "#543ba4",
    borderColor: "#543ba4",
    color: "white",
  },
  buttonSecondary: {
    borderColor: "#6c757d",
    color: "#6c757d",
  },
};

// Configuration des status de paiement et leurs messages associés
const paymentStatuses = {
  paid: {
    title: "Paiement confirmé",
    message: "Votre commande a été enregistrée avec succès.",
    icon: "bi-check-circle-fill text-success",
  },
  canceled: {
    title: "Paiement annulé",
    message: "Vous avez annulé votre paiement. Votre panier a été conservé.",
    icon: "bi-x-circle-fill text-danger",
  },
  expired: {
    title: "Paiement expiré",
    message: "Votre session de paiement a expiré. Vous pouvez réessayer.",
    icon: "bi-clock-fill text-warning",
  },
  failed: {
    title: "Échec du paiement",
    message:
      "Le paiement a échoué. Veuillez réessayer ou utiliser un autre moyen de paiement.",
    icon: "bi-exclamation-triangle-fill text-danger",
  },
  pending: {
    title: "Paiement en attente",
    message:
      "Votre paiement est en cours de traitement. Nous vous informerons dès qu'il sera confirmé.",
    icon: "bi-hourglass-split text-primary",
  },
  open: {
    title: "Paiement en attente",
    message:
      "Votre paiement n'est pas encore finalisé. Veuillez suivre les instructions fournies par votre banque.",
    icon: "bi-hourglass-split text-primary",
  },
  unknown: {
    title: "Statut inconnu",
    message:
      "Le statut de votre paiement est inconnu. Veuillez contacter notre service client.",
    icon: "bi-question-circle-fill text-secondary",
  },
};

const PaymentResult = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();

  const [paymentStatus, setPaymentStatus] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingExpired, setCheckingExpired] = useState(false);
  const [statusDetermined, setStatusDetermined] = useState(false);

  // Fonction pour nettoyer le localStorage
  const cleanupLocalStorage = () => {
    console.log("Nettoyage du localStorage pour les données de paiement");
    localStorage.removeItem("currentOrderId");
    localStorage.removeItem("molliePaymentId");
    localStorage.removeItem("paymentStartTime");
  };

  useEffect(() => {
    // Récupérer les paramètres
    const orderParam = searchParams.get("order");
    const statusParam = searchParams.get("status");
    const originParam = searchParams.get("origin");

    console.log("Paramètres URL:", {
      order: orderParam,
      status: statusParam,
      origin: originParam,
    });

    // Récupérer les données stockées dans le localStorage
    const storedOrderId = localStorage.getItem("currentOrderId");
    const storedPaymentId = localStorage.getItem("molliePaymentId");
    const paymentStartTime = localStorage.getItem("paymentStartTime");

    console.log("Données localStorage:", {
      storedOrderId,
      storedPaymentId,
      paymentStartTime: paymentStartTime
        ? new Date(parseInt(paymentStartTime)).toISOString()
        : null,
    });

    // Définir l'ID de commande depuis l'URL ou le localStorage
    setOrderId(orderParam || storedOrderId);

    // Détecter et sortir des redirections en boucle
    if (statusParam === "expired" && originParam === "auto") {
      console.log(
        "Détection d'une possible boucle de redirection - nettoyage et arrêt"
      );
      setPaymentStatus("expired");
      setStatusDetermined(true);
      cleanupLocalStorage();
      setLoading(false);
      return;
    }

    // Vérifier si le paiement pourrait être expiré
    const isLikelyExpired =
      paymentStartTime &&
      Date.now() - parseInt(paymentStartTime) > 15 * 60 * 1000; // 15 minutes

    const checkPaymentStatus = async () => {
      try {
        setLoading(true);

        // Si status est fourni explicitement dans l'URL
        if (statusParam) {
          console.log(`Status explicite dans l'URL: ${statusParam}`);
          setPaymentStatus(statusParam);
          setStatusDetermined(true);

          // Nettoyer le panier si le paiement est réussi
          if (statusParam === "paid") {
            console.log("Paiement réussi - nettoyage du panier");
            clearCart();
          }

          // Ne nettoyer le localStorage qu'après avoir bien détecté le statut
          if (
            statusParam === "expired" ||
            statusParam === "canceled" ||
            statusParam === "failed"
          ) {
            cleanupLocalStorage();
          }

          setLoading(false);
          return;
        }

        // Si nous sommes sur cette page mais sans status, c'est probablement une expiration
        if (originParam === "redirect" && isLikelyExpired) {
          console.log(
            "Paiement probablement expiré (basé sur le temps écoulé)"
          );
          setCheckingExpired(true);
        }

        // Vérifier le statut via l'API si nous avons un ID de paiement
        if (storedPaymentId) {
          console.log(
            `Vérification du statut pour le paiement: ${storedPaymentId}`
          );

          try {
            const result = await mollieService.checkPaymentStatus(
              storedPaymentId
            );
            console.log("Résultat de la vérification:", result);

            if (result && result.status) {
              console.log(`Statut déterminé depuis l'API: ${result.status}`);
              setPaymentStatus(result.status);
              setStatusDetermined(true);

              // Nettoyer le panier si le paiement est réussi
              if (result.status === "paid") {
                console.log("Paiement réussi - nettoyage du panier");
                clearCart();
              }

              // Ne nettoyer le localStorage qu'après avoir bien détecté le statut
              if (
                result.status === "expired" ||
                result.status === "canceled" ||
                result.status === "failed"
              ) {
                cleanupLocalStorage();
              }
            } else {
              // Si aucun résultat n'est retourné et que le paiement a probablement expiré
              if (isLikelyExpired) {
                console.log(
                  "Définir le statut comme 'expired' car aucun résultat et temps écoulé important"
                );
                setPaymentStatus("expired");
                setStatusDetermined(true);
                cleanupLocalStorage();
              } else {
                console.log(
                  "Aucun statut n'a pu être déterminé - utilisé 'unknown'"
                );
                setPaymentStatus("unknown");
                setStatusDetermined(true);
              }
            }
          } catch (apiError) {
            console.error(
              "Erreur lors de la vérification du statut via l'API:",
              apiError
            );

            if (isLikelyExpired) {
              setPaymentStatus("expired");
            } else {
              setPaymentStatus("unknown");
            }
            setStatusDetermined(true);
          }
        } else {
          // Si aucun ID de paiement n'est disponible
          console.log("Aucun ID de paiement disponible - statut inconnu");
          setPaymentStatus("unknown");
          setStatusDetermined(true);
        }
      } catch (error) {
        console.error(
          "Erreur générale lors de la vérification du statut:",
          error
        );

        // En cas d'erreur, si le paiement a probablement expiré
        if (isLikelyExpired) {
          console.log(
            "Définir le statut comme 'expired' en raison de l'erreur et du temps écoulé"
          );
          setPaymentStatus("expired");
          setStatusDetermined(true);
          cleanupLocalStorage();
        } else {
          console.log("Erreur lors de la vérification - statut inconnu");
          setPaymentStatus("unknown");
          setStatusDetermined(true);
        }
      } finally {
        setLoading(false);
        setCheckingExpired(false);
      }
    };

    checkPaymentStatus();

    // Nettoyer le localStorage après que le composant soit démonté
    // et que l'interface ait eu le temps d'afficher correctement le statut
    return () => {
      // Nettoyer uniquement si le statut a été déterminé
      if (statusDetermined) {
        console.log("Nettoyage du localStorage lors du démontage du composant");
        cleanupLocalStorage();
      }
    };
  }, [searchParams, clearCart]);

  // Éventuellement, nettoyer le localStorage après un temps défini
  // pour éviter qu'un statut ne reste affiché trop longtemps
  useEffect(() => {
    // Si le statut a été déterminé, planifier un nettoyage après 5 minutes
    // Cela laisse le temps à l'utilisateur de voir le résultat
    if (statusDetermined && paymentStatus) {
      const cleanupTimeout = setTimeout(() => {
        if (paymentStatus === "paid" || paymentStatus === "pending") {
          console.log(
            "Nettoyage du localStorage après temporisation (statut confirmé)"
          );
          cleanupLocalStorage();
        }
      }, 5 * 60 * 1000); // 5 minutes

      return () => clearTimeout(cleanupTimeout);
    }
  }, [statusDetermined, paymentStatus]);

  // Gérer un nouveau paiement (pour les paiements échoués ou expirés)
  const handleRetryPayment = () => {
    console.log("Redirection vers le checkout avec nettoyage");
    cleanupLocalStorage();
    navigate("/checkout");
  };

  // Afficher un loader pendant la vérification
  if (loading) {
    return (
      <div className="container text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
        <p className="mt-2">
          {checkingExpired
            ? "Vérification du statut de votre paiement..."
            : "Traitement de votre paiement..."}
        </p>
      </div>
    );
  }

  // Vérifier si nous avons des informations de commande
  if (
    !orderId &&
    paymentStatus !== "canceled" &&
    paymentStatus !== "failed" &&
    paymentStatus !== "expired"
  ) {
    return (
      <div style={styles.container}>
        <div style={styles.card} className="text-center">
          <i
            className="bi bi-exclamation-circle-fill text-danger"
            style={styles.icon}
          ></i>
          <h2 className="mb-3">Une erreur est survenue</h2>
          <p className="mb-4">Informations de commande introuvables</p>

          <div className="d-flex justify-content-center gap-3">
            <Link to="/" className="btn btn-lg" style={styles.buttonPrimary}>
              <i className="bi bi-house-door me-2"></i>
              Retour à l'accueil
            </Link>
            <Link to="/contact" className="btn btn-lg btn-outline-secondary">
              <i className="bi bi-chat-dots me-2"></i>
              Contacter le support
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Déterminer le statut à afficher
  const status = paymentStatuses[paymentStatus] || paymentStatuses.unknown;

  return (
    <div style={styles.container}>
      <div style={styles.card} className="text-center">
        <i className={`bi ${status.icon}`} style={styles.icon}></i>
        <h2 className="mb-3">{status.title}</h2>
        <p className="mb-4">{status.message}</p>

        {orderId &&
          (paymentStatus === "paid" || paymentStatus === "pending") && (
            <p className="text-muted mb-4">Référence de commande: #{orderId}</p>
          )}

        <div className="d-flex justify-content-center gap-3">
          {(paymentStatus === "canceled" ||
            paymentStatus === "failed" ||
            paymentStatus === "expired") && (
            <button
              className="btn btn-lg"
              style={styles.buttonPrimary}
              onClick={handleRetryPayment}
            >
              <i className="bi bi-arrow-repeat me-2"></i>
              Réessayer le paiement
            </button>
          )}

          <Link
            to="/"
            className="btn btn-lg"
            style={
              paymentStatus === "canceled" ||
              paymentStatus === "failed" ||
              paymentStatus === "expired"
                ? styles.buttonSecondary
                : styles.buttonPrimary
            }
          >
            <i className="bi bi-house-door me-2"></i>
            Retour à l'accueil
          </Link>
        </div>
      </div>

      {/* Informations supplémentaires */}
      <div style={styles.card}>
        <h4 className="mb-3">Que se passe-t-il maintenant ?</h4>

        {paymentStatus === "paid" && (
          <div>
            <p>
              Nous avons reçu votre paiement et votre commande est confirmée.
            </p>
            <ol className="mb-0">
              <li className="mb-2">
                Un e-mail de confirmation vous a été envoyé.
              </li>
              <li className="mb-2">
                Votre commande est en cours de préparation.
              </li>
              <li>
                Vous recevrez un e-mail avec les informations de suivi lorsque
                votre commande sera expédiée.
              </li>
            </ol>
          </div>
        )}

        {paymentStatus === "pending" && (
          <div>
            <p>Votre paiement est en cours de traitement.</p>
            <ol className="mb-0">
              <li className="mb-2">
                Le traitement peut prendre quelques minutes à quelques heures.
              </li>
              <li className="mb-2">
                Vous recevrez un e-mail dès que le paiement sera confirmé.
              </li>
              <li>
                Vous pouvez vérifier l'état de votre commande dans votre compte.
              </li>
            </ol>
          </div>
        )}

        {(paymentStatus === "canceled" ||
          paymentStatus === "failed" ||
          paymentStatus === "expired") && (
          <div>
            <p>
              Votre paiement n'a pas été finalisé, mais ne vous inquiétez pas :
            </p>
            <ol className="mb-0">
              <li className="mb-2">
                Vos articles sont toujours dans votre panier.
              </li>
              <li className="mb-2">
                Vous pouvez réessayer le paiement ou choisir une autre méthode.
              </li>
              <li>
                Notre service client est disponible pour vous aider si
                nécessaire.
              </li>
            </ol>
          </div>
        )}

        {paymentStatus === "unknown" && (
          <div>
            <p>Nous n'avons pas pu déterminer l'état de votre paiement.</p>
            <ol className="mb-0">
              <li className="mb-2">
                Vérifiez votre compte bancaire pour voir si le montant a été
                débité.
              </li>
              <li className="mb-2">
                Consultez votre boîte e-mail pour un message de confirmation.
              </li>
              <li>
                Contactez notre service client si vous avez besoin d'aide.
              </li>
            </ol>
          </div>
        )}
      </div>

      {/* Bloc de contact */}
      <div style={styles.card}>
        <h4 className="mb-3">Besoin d'aide ?</h4>
        <p>
          Notre équipe de service client est disponible pour répondre à vos
          questions :
        </p>
        <div className="row mt-4">
          <div className="col-md-6 mb-3 mb-md-0">
            <div className="d-flex align-items-center mb-2">
              <i className="bi bi-envelope me-2 text-primary"></i>
              <span>support@boxplaisir.be</span>
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex align-items-center">
              <i className="bi bi-telephone me-2 text-primary"></i>
              <span>+32 123 456 789</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentResult;
